import { Controller } from "@hotwired/stimulus"

const VERTEX_SRC = `
  attribute vec2 a_position;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`

const FRAGMENT_SRC = `
  precision highp float;
  uniform vec2 u_resolution;
  uniform float u_time;

  // Hash-based pseudo-random
  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }

  // Smooth noise
  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  // Fractal brownian motion
  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    vec2 shift = vec2(100.0);
    for (int i = 0; i < 5; i++) {
      v += a * noise(p);
      p = p * 2.0 + shift;
      a *= 0.5;
    }
    return v;
  }

  // Spark particle
  float spark(vec2 uv, vec2 pos, float size) {
    float d = length(uv - pos);
    return smoothstep(size, 0.0, d) * (1.0 / (1.0 + d * 40.0));
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution;
    float aspect = u_resolution.x / u_resolution.y;
    vec2 p = uv;
    p.x *= aspect;

    float t = u_time * 0.3;

    // Deep charcoal/navy base with subtle gradient
    vec3 bgTop = vec3(0.06, 0.07, 0.12);
    vec3 bgBot = vec3(0.10, 0.05, 0.04);
    vec3 col = mix(bgBot, bgTop, uv.y);

    // Molten glow from the bottom — forge hearth
    float furnaceGlow = smoothstep(0.35, 0.0, uv.y);
    furnaceGlow *= 0.6 + 0.4 * sin(t * 1.5 + uv.x * 6.0);
    vec3 moltenColor = mix(vec3(0.85, 0.25, 0.02), vec3(1.0, 0.55, 0.05), uv.y * 3.0);
    col += moltenColor * furnaceGlow * 0.5;

    // Flowing heat distortion / lava streaks
    float flow1 = fbm(vec2(p.x * 3.0, p.y * 2.0 - t * 0.8));
    float flow2 = fbm(vec2(p.x * 2.5 + 50.0, p.y * 3.0 - t * 0.6));
    float flowMask = smoothstep(0.30, 0.0, uv.y);
    vec3 lavaCol = mix(vec3(0.9, 0.15, 0.0), vec3(1.0, 0.6, 0.1), flow1);
    col += lavaCol * flow1 * flow2 * flowMask * 0.4;

    // Rising heat haze / energy waves
    float haze = fbm(vec2(p.x * 4.0 + t * 0.2, p.y * 6.0 - t * 1.2));
    float hazeMask = smoothstep(0.0, 0.5, uv.y) * smoothstep(1.0, 0.5, uv.y);
    col += vec3(0.3, 0.12, 0.05) * haze * hazeMask * 0.3;

    // Sparks rising from the forge
    for (int i = 0; i < 18; i++) {
      float fi = float(i);
      float seed = hash(vec2(fi * 17.3, fi * 31.7));
      float sparkTime = fract(t * (0.15 + seed * 0.25) + seed * 10.0);

      // Spark position — rises from bottom
      float sx = (seed * 0.8 + 0.1) * aspect;
      sx += sin(sparkTime * 6.28 + seed * 20.0) * 0.08 * (1.0 + seed);
      float sy = sparkTime;

      // Spark properties
      float sparkLife = 1.0 - sparkTime;
      float sparkSize = 0.008 * sparkLife * (0.5 + seed * 0.5);
      float brightness = sparkLife * sparkLife;

      // Warm spark color — orange to gold
      vec3 sparkCol = mix(vec3(1.0, 0.4, 0.05), vec3(1.0, 0.8, 0.2), seed);

      float s = spark(p, vec2(sx, sy), sparkSize);
      col += sparkCol * s * brightness * 1.5;
    }

    // Subtle blue brand accent — cool highlights at the top
    float blueAccent = smoothstep(0.6, 1.0, uv.y);
    float blueNoise = fbm(vec2(p.x * 3.0 + t * 0.1, p.y * 2.0));
    col += vec3(0.1, 0.2, 0.5) * blueAccent * blueNoise * 0.15;

    // Central radiant energy pulse
    vec2 center = vec2(aspect * 0.5, 0.35);
    float pulse = sin(t * 2.0) * 0.3 + 0.7;
    float radiance = 1.0 / (1.0 + length(p - center) * 4.0);
    col += vec3(1.0, 0.5, 0.1) * radiance * pulse * 0.15;

    // Vignette
    float vig = 1.0 - length((uv - 0.5) * 1.4);
    vig = smoothstep(0.0, 0.7, vig);
    col *= vig;

    // Tone mapping
    col = col / (1.0 + col);
    col = pow(col, vec3(0.9));

    gl_FragColor = vec4(col, 1.0);
  }
`

export default class extends Controller {
  connect() {
    this._canvas = this.element
    this._gl = this._canvas.getContext("webgl")
    if (!this._gl) return

    this._initShaders()
    this._initGeometry()
    this._resize()
    this._startTime = performance.now()
    this._animating = true

    this._resizeObserver = new ResizeObserver(() => this._resize())
    this._resizeObserver.observe(this._canvas.parentElement)

    document.addEventListener("visibilitychange", this._onVisibility)
    this._tick()
  }

  disconnect() {
    this._animating = false
    if (this._raf) cancelAnimationFrame(this._raf)
    if (this._resizeObserver) this._resizeObserver.disconnect()
    document.removeEventListener("visibilitychange", this._onVisibility)

    const gl = this._gl
    if (gl && this._program) {
      gl.deleteProgram(this._program)
      gl.deleteBuffer(this._buffer)
    }
  }

  _onVisibility = () => {
    if (document.hidden) {
      this._animating = false
      if (this._raf) cancelAnimationFrame(this._raf)
    } else {
      this._animating = true
      this._tick()
    }
  }

  _initShaders() {
    const gl = this._gl
    const vs = this._compile(gl.VERTEX_SHADER, VERTEX_SRC)
    const fs = this._compile(gl.FRAGMENT_SHADER, FRAGMENT_SRC)

    this._program = gl.createProgram()
    gl.attachShader(this._program, vs)
    gl.attachShader(this._program, fs)
    gl.linkProgram(this._program)
    gl.useProgram(this._program)

    this._uResolution = gl.getUniformLocation(this._program, "u_resolution")
    this._uTime = gl.getUniformLocation(this._program, "u_time")
  }

  _compile(type, source) {
    const gl = this._gl
    const shader = gl.createShader(type)
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    return shader
  }

  _initGeometry() {
    const gl = this._gl
    const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1])
    this._buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this._buffer)
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW)

    const pos = gl.getAttribLocation(this._program, "a_position")
    gl.enableVertexAttribArray(pos)
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0)
  }

  _resize() {
    const parent = this._canvas.parentElement
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const w = parent.clientWidth
    const h = parent.clientHeight

    this._canvas.width = w * dpr
    this._canvas.height = h * dpr
    this._canvas.style.width = w + "px"
    this._canvas.style.height = h + "px"

    this._gl.viewport(0, 0, this._canvas.width, this._canvas.height)
  }

  _tick = () => {
    if (!this._animating) return

    const gl = this._gl
    const elapsed = (performance.now() - this._startTime) / 1000

    gl.uniform2f(this._uResolution, this._canvas.width, this._canvas.height)
    gl.uniform1f(this._uTime, elapsed)
    gl.drawArrays(gl.TRIANGLES, 0, 6)

    this._raf = requestAnimationFrame(this._tick)
  }
}
